"use client"

import dynamic from 'next/dynamic'
import {
  SERVICE_QUOTE_SURFACES,
  getProblemIntentFunnel,
  isHireIntentServiceSlug,
  isRegionalServiceSlug,
} from '@/data/service-quote-funnels'
import { ContextualToolCTA } from '@/components/blog/contextual-tool-cta'
// Static import (not dynamic): the strip is above the fold on the grass-by-state
// guides and must be in the server-rendered HTML, not a client-only fill-in.
import { GrassSpeciesStrip } from '@/components/blog/grass-species-strip'

const MiniFertilizerCalculator = dynamic(
  () => import('@/components/tools/mini-fertilizer-calculator').then(m => m.MiniFertilizerCalculator),
  { loading: () => <WidgetSkeleton label="Fertilizer Calculator" /> }
)

const MiniHerbicideTiming = dynamic(
  () => import('@/components/tools/mini-herbicide-timing').then(m => m.MiniHerbicideTiming),
  { loading: () => <WidgetSkeleton label="Herbicide Timing Calculator" /> }
)

const InlineProductCallout = dynamic(
  () => import('@/components/blog/inline-product-callout').then(m => m.InlineProductCallout),
  { loading: () => null }
)

const CategoryProductCallout = dynamic(
  () => import('@/components/blog/category-product-callout').then(m => m.CategoryProductCallout),
  { loading: () => null }
)

const ComparisonTable = dynamic(
  () => import('@/components/blog/comparison-table').then(m => m.ComparisonTable),
  { loading: () => null }
)

const ReminderWidget = dynamic(
  () => import('@/components/blog/reminder-widget').then(m => m.ReminderWidget),
  { loading: () => null }
)

const InlineAppDownload = dynamic(
  () => import('@/components/blog/inline-app-download').then(m => m.InlineAppDownload),
  { loading: () => null }
)

const ServiceQuoteCTA = dynamic(
  () => import('@/components/service-leads/service-quote-cta').then(m => m.ServiceQuoteCTA),
  { loading: () => null }
)

function WidgetSkeleton({ label }: { label: string }) {
  return (
    <div className="my-8 mx-auto max-w-md rounded-xl border border-green-200 bg-green-50/50 p-6 animate-pulse">
      <div className="h-5 w-40 bg-green-200 rounded mb-4" />
      <div className="h-10 w-full bg-green-100 rounded mb-3" />
      <div className="h-10 w-full bg-green-100 rounded mb-3" />
      <div className="h-10 w-32 bg-green-300 rounded" />
      <p className="text-xs text-gray-400 mt-3 text-center">Loading {label}...</p>
    </div>
  )
}

const WIDGET_MAP: Record<string, React.ComponentType<any>> = {
  'fertilizer-calculator': MiniFertilizerCalculator,
  'herbicide-timing': MiniHerbicideTiming,
  'product-pick': InlineProductCallout,
  'category-product': CategoryProductCallout,
  'comparison-table': ComparisonTable,
  'reminder-widget': ReminderWidget,
  'service-quote-cta': ServiceQuoteCTA,
  'app-download': InlineAppDownload,
}

interface InlineToolWidgetProps {
  tool: string
  variant?: string
  slug?: string
  category?: string
  tags?: string[]
  payload?: string
}

export function InlineToolWidget({ tool, variant, slug, category, tags, payload }: InlineToolWidgetProps) {
  if (tool === 'species-strip') {
    return <GrassSpeciesStrip payload={payload} />
  }
  // The blog quote card only earns its slot where the reader is shopping for a
  // contractor (hire-intent posts). The regional and problem-intent modes are
  // retired — both measured at ~0% and the slot monetizes better as an
  // affiliate/tool CTA — but they stay wired so a flag flip brings them back.
  // Hire wins first: city-services slugs are also regional slugs. Each mode has
  // its own sourcePlacement (= CJ sid) so CTR stays separable, and the retired
  // sids keep their history rather than absorbing the new placement's data.
  // Hire-intent cards render from markers hardcoded in the Directus bodies; the
  // server injector gates on blogInline/blogProblemInline only.
  if (tool === 'service-quote-cta') {
    const hire = SERVICE_QUOTE_SURFACES.blogHireIntent && isHireIntentServiceSlug(slug)
    const regional = !hire && SERVICE_QUOTE_SURFACES.blogInline && isRegionalServiceSlug(slug)
    const problem =
      !hire &&
      !regional &&
      SERVICE_QUOTE_SURFACES.blogProblemInline &&
      getProblemIntentFunnel(slug, category, tags) !== null
    if (!hire && !regional && !problem) return null
    return (
      <ServiceQuoteCTA
        variant={variant}
        slug={slug}
        tags={tags}
        sourcePlacement={
          hire ? 'blog_hire_intent' : regional ? 'blog_inline_service_quote' : 'blog_problem_inline'
        }
      />
    )
  }
  // Early-CTA pilot (item 1.5): render the SAME intent surface the mid-content
  // ContextualToolCTA would choose, tagged slot='early' so its analytics stay
  // separable. The component re-derives intent from slug/category/tags, so the
  // marker's data-variant is a hint only.
  if (tool === 'early-cta') {
    return <ContextualToolCTA slug={slug} category={category} tags={tags} slot="early" />
  }
  const Widget = WIDGET_MAP[tool]
  if (!Widget) return null
  // Keyed on the slug so an App Router soft navigation between two posts that
  // both carry this widget remounts it instead of updating props in place.
  // Impression hooks fire once per mount and re-arm only when their `source`
  // changes; `source` here is the cta_location, which is deliberately the same
  // on every post so the rate aggregates. Without the key the second post's
  // impression is never counted. (Found by review, 2026-08-09.)
  return <Widget key={slug} variant={variant} slug={slug} tags={tags} />
}
